<template>
  <v-card outlined class="premium-summary-card" :class="{ '--is-black-friday': isBlackFriday }" style="border-radius: 8px">
    <v-card-title class="text-h6 pb-2">
      <v-icon left>mdi-format-list-bulleted</v-icon>
      Deine Auswahl
    </v-card-title>

    <v-list dense subheader style="background: transparent !important" class="py-0">
      <div v-if="selectedPackages.length === 0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-center grey--text">Keine Pakete ausgewählt</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>

      <transition-group appear name="list-item" tag="div">
        <v-list-item
          v-for="(pck, index) in selectedPackages"
          :key="pck.packageName"
          two-line
          class="list-item"
          :style="{ transitionDelay: `${index * 100}ms` }"
          ><v-list-item-content>
            <v-list-item-title class="font-weight-medium" style="font-size: 1rem !important">
              <span v-if="pck.amountSelectable && pck.amount">{{ pck.amount }}x </span>
              {{ pck.packageName }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="isPelviAnteilPackage(pck)"> (Anteilig für lfd. Vertragsjahr) </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <div v-if="isBlackFriday" class="text-right">
              <div class="font-weight-bold" style="font-size: 1rem">
                <span v-if="isPelviAnteilPackage(pck)">
                  {{ displayCurrency(calcPelvixPrice(pck, true)) }}
                </span>
                <span v-else-if="pck.amountSelectable && pck.amount">
                  {{ displayCurrency(pck.amount * parseInt(pck.price) * 0.8) }}
                </span>
                <span v-else>
                  {{ displayCurrency(pck.price * 0.8) }}
                </span>
              </div>
              <div class="grey--text text--lighten-1 text-decoration-line-through" style="font-size: 0.9rem">
                <span v-if="isPelviAnteilPackage(pck)">
                  {{ displayCurrency(calcPelvixPrice(pck, false)) }}
                </span>
                <span v-else-if="pck.amountSelectable && pck.amount">
                  {{ displayCurrency(pck.amount * parseInt(pck.price)) }}
                </span>
                <span v-else>
                  {{ displayCurrency(pck.price) }}
                </span>
              </div>
            </div>

            <div v-else class="text-right font-weight-bold" style="font-size: 1rem">
              <span v-if="isPelviAnteilPackage(pck)">
                {{ displayCurrency(calcPelvixPrice(pck, false)) }}
              </span>
              <span v-else-if="pck.amountSelectable && pck.amount">
                {{ displayCurrency(pck.amount * parseInt(pck.price)) }}
              </span>
              <span v-else>
                {{ displayCurrency(pck.price) }}
              </span>
            </div>
          </v-list-item-action>
        </v-list-item>
      </transition-group>
    </v-list>

    <v-divider class="mt-2" dark></v-divider>

    <v-card-text class="pa-4">
      <template v-if="isBlackFriday">
        <div class="text-subtitle-1 d-flex justify-space-between">
          <span>Zwischensumme:</span>
          <span>{{ displayCurrency(originalTotalPrice) }}</span>
        </div>
        <div class="text-subtitle-1 d-flex justify-space-between" style="color: #ffd700; font-weight: bold">
          <span>
            <v-icon left small color="#ffd700">mdi-sale</v-icon>
            Black Friday Rabatt (20%):
          </span>
          <span>- {{ displayCurrency(totalDiscount) }}</span>
        </div>
        <v-divider dark class="my-2"></v-divider>
        <div class="text-h6 d-flex justify-space-between final-price-label">
          <span>Heute fällig:</span> <span class="font-weight-bold">{{ displayCurrency(totalPrice) }}</span>
        </div>
      </template>

      <template v-else>
        <div class="text-h6 d-flex justify-space-between final-price-label">
          <span>Heute fällig:</span> <span class="font-weight-bold">{{ displayCurrency(totalPrice) }}</span>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
// Stell sicher, dass der Pfad zu deinem Currency-Modul stimmt!
import { roundToNearestFiveCents } from "@/moduls/currency/currency.js"

export default {
  name: "UpgradeSummary",
  props: {
    selectedPackages: {
      type: Array,
      default: () => [],
    },
    isBlackFriday: {
      type: Boolean,
      default: false,
    },
    originalTotalPrice: {
      type: Number,
      default: 0,
    },
    totalDiscount: {
      type: Number,
      default: 0,
    },
    totalPrice: {
      type: Number,
      required: true,
    },
    leftTrainingYears: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    displayCurrency(amount) {
      return roundToNearestFiveCents(amount)
    },
    isPelviAnteilPackage(pck) {
      if (!pck || !pck.packageName) return false
      return pck.packageName.toLowerCase().includes("pelvi") && pck.packageName.toLowerCase().includes("anteil")
    },
    calcPelvixPrice(pck, applyDiscount = true) {
      let price = pck.price - this.leftTrainingYears * pck.usualPriceYear

      if (this.isBlackFriday && applyDiscount) {
        price = price * 0.8
      }
      return price
    },
  },
  computed: {
    // Nicht mehr benötigt, da Farben/Stile jetzt inline oder
    // über Standard-Vuetify-Klassen (z.B. font-weight-bold) gesteuert werden.
  },
}
</script>

<style lang="scss" scoped>
/* STYLES FÜR DEN "PREMIUM" LOOK */
.premium-summary-card {
  background: linear-gradient(145deg, #333333, #2a2a2a);
  border: 1px solid #444;
  border-left: 4px solid var(--v-primary-base);
  transition: all 0.3s ease;

  &.--is-black-friday {
    border-left: 4px solid #ffd700;
    box-shadow: 0 8px 20px rgba(255, 215, 0, 0.1);
  }
}

/* Passt das Label für den Endpreis an */
.final-price-label {
  color: #ffffff;
}

.v-list-item__title {
  font-size: 1rem !important;
  white-space: normal;
}
.v-list-item__action {
  min-width: 110px;
}

/* ANIMATIONS-KLASSEN GEFIXT 
  Diese definieren, WIE animiert wird.
  Das 'appear' im Template sagt, DASS animiert wird.
  Der ':style' im Template sagt, WANN (Stagger).
*/
.list-item-enter-active {
  /* Definiert die Animationsdauer und -kurve */
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.list-item-enter {
  /* Start-Zustand (vor der Animation) */
  opacity: 0;
  transform: translateY(20px);
}
/* (Der End-Zustand ist der normale CSS-Zustand: opacity: 1, transform: translateY(0)) */
</style>
